import React, { memo, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { areEqual } from 'Utils/equalityChecks';
import { DeleteReportModal, ReportsTable, ShareReportModal } from 'Components/ReportsAndDocuments';
import type { UserModel } from 'Containers/User/Models/UserModel';
import { useUser } from 'Context/User';
import { reportTableHeaders } from 'Utils/table';
import { useDispatch, useSelector } from 'react-redux';
import {
  emailErrorSelector,
  reportDeletedSelector,
  reportSharedSelector,
  reportSharingSelector,
  reportsSelector,
} from 'Containers/ReportsAndDocuments/Reports/selectors';
import { projectSelector } from 'Containers/RocketScan/selectors';
import {
  deleteReport,
  listProjectReports,
  setSelectedReportEdit,
  setReportDeleted,
  setReportShared,
  shareReport,
} from 'Containers/ReportsAndDocuments/Reports/actions';
import { DownloadReportModal } from 'Components/ReportsAndDocuments/Reports/DownloadReportModal';
import { download } from 'Utils/helpers';
import { pusherSelector } from 'Containers/Core/selectors';
import { GeneralToast } from 'Components/Toast';
import { userFeatureFlagsSelector } from 'Containers/User/selector';

function ReportsTableContainer() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { isCompanyAdmin }: UserModel = useUser();

  const [headers, setHeaders] = useState([]);
  const [sortBy, setSortBy] = useState('-id');
  const [initialPage, setInitialPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [fetchingReports, setFetchingReports] = useState(false);
  const [email, setEmail] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const project = useSelector(projectSelector, areEqual);
  const reports = useSelector(reportsSelector, areEqual);
  const reportDeleted = useSelector(reportDeletedSelector, areEqual);
  const reportShared = useSelector(reportSharedSelector, areEqual);
  const reportSharing = useSelector(reportSharingSelector, areEqual);
  const pusher = useSelector(pusherSelector, areEqual);

  // feature flag selector
  const { hideDeleteButton } = useSelector(userFeatureFlagsSelector, areEqual);

  const itemsPerPage = 10;

  /*
   * Reports table functions
   * */
  useEffect(() => {
    setHeaders(reportTableHeaders.filter((header: any) => (isCompanyAdmin ? header : header.column !== 'delete')));
  }, [isCompanyAdmin]);

  const getReports = useCallback(
    (skipSetFetching: boolean = false) => {
      const { id: projectId } = project;
      dispatch(
        listProjectReports(
          projectId,
          sortBy,
          initialPage + 1,
          itemsPerPage,
          skipSetFetching ? null : setFetchingReports
        )
      );
    },
    [project, sortBy, initialPage]
  );

  useEffect(() => {
    if (project?.id) {
      getReports();
    }
  }, [project, sortBy, initialPage]);

  // Polling for reports with status 'Processing'
  useEffect(() => {
    let interval;
    if (reports?.data?.some((report) => report.status === 'Processing')) {
      interval = setInterval(() => {
        getReports(true);
      }, 2500);
    } else if (interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [reports]);

  // set meta data
  useEffect(() => {
    if (reports?.data?.length > 0) {
      const { meta } = reports;
      const { total } = meta;

      setPageCount(total <= itemsPerPage ? 1 : Math.ceil(total / itemsPerPage));
    }
  }, [reports]);

  const onClickSort = useCallback((sort: string) => {
    setSortBy((prevSortBy) => (prevSortBy.includes('-') ? prevSortBy.replace('-', '') : sort));
  }, []);

  const onPageChange = useCallback(({ selected: page }: any) => {
    setInitialPage(page);
  }, []);

  const onDownloadIconClick = useCallback(
    (report: any) => {
      setSelectedReport(report);
      setShowDownloadModal(true);
    },
    [reports]
  );

  const displayToast = useCallback(() => {
    setToastMessage(
      "To download a Custom Report, open up the report by clicking the 'Edit' button. While on the report click: File - Export - PDF"
    );
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 2500);
  }, []);

  const onClickToastClose = useCallback(() => setShowToast(false), []);

  const onShareIconClick = useCallback((report: any) => {
    setSelectedReport(report);
    setShowShareModal(true);
  }, []);

  const onDeleteIconClick = useCallback(
    (report: any) => {
      setSelectedReport(report);
      setShowDeleteModal(true);
    },
    [reports]
  );

  const onEditIconClick = useCallback(
    (report: any) => {
      const { id: projectId } = project;
      dispatch(setSelectedReportEdit(report));
      history.push(`/projects/${projectId}/reports/generate/custom`);
    },
    [project]
  );

  /*
   * Reports download functions
   * */
  const onDownloadButtonClick = useCallback(async () => {
    if (selectedReport) {
      setLoading(true);
      const { name, pdf_url: url } = selectedReport;
      await download(url, name, setLoading);
    }
  }, [selectedReport]);

  /*
   * Reports share functions
   * */
  const errors = {
    email: useSelector(emailErrorSelector, areEqual),
  };

  const onChangeEmail = useCallback(({ target: { value } }: any) => {
    setEmail(value);
  }, []);

  const onFormSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      const { id } = selectedReport;

      dispatch(shareReport(id, { email }));
    },
    [email, selectedReport]
  );

  useEffect(() => {
    if (reportShared) {
      setShowShareModal(false);
      dispatch(setReportShared(false));
    }

    return () => {
      if (reportShared) {
        dispatch(setReportShared(false));
      }
    };
  }, [reportShared]);

  /*
   * Reports delete functions
   * */
  const onDeleteButtonClick = useCallback(() => {
    if (selectedReport) {
      setLoading(true);
      const { id } = selectedReport;
      dispatch(deleteReport(id));
    }
  }, [selectedReport]);

  // refresh on delete
  useEffect(() => {
    if (reportDeleted) {
      getReports();
      setShowDeleteModal(false);
      setLoading(false);
      dispatch(setReportDeleted(false));
    }

    return () => {
      if (reportDeleted) {
        dispatch(setReportDeleted(false));
      }
    };
  }, [reportDeleted]);

  /*
   * Reports common functions
   * */

  const onCancelButtonClick = useCallback((e: any) => {
    e.preventDefault();
    setTimeout(() => setSelectedReport(undefined), 500);
    setShowDeleteModal(false);
    setShowDownloadModal(false);
    setShowShareModal(false);
  }, []);

  // to refresh reports on completed
  useEffect(() => {
    if (project?.id) {
      const { id: projectId } = project;
      pusher
        ?.subscribe(`BroadcastProjectReportGeneratedEvent.Project.${projectId}`)
        ?.bind('App\\Events\\BroadcastProjectReportGeneratedEvent', getReports);
    }

    return () => {
      if (project?.id) {
        const { id: projectId } = project;
        pusher.unsubscribe(`BroadcastProjectReportGeneratedEvent.Project.${projectId}`);
      }
    };
  }, [pusher, project]);

  return (
    <>
      <ReportsTable
        headers={headers}
        reports={reports}
        sortBy={sortBy}
        initialPage={initialPage}
        pageCount={pageCount}
        isCompanyAdmin={isCompanyAdmin}
        canDelete={!hideDeleteButton}
        fetching={fetchingReports}
        onClickSort={onClickSort}
        onPageChange={onPageChange}
        onDownloadIconClick={onDownloadIconClick}
        displayToast={displayToast}
        onShareIconClick={onShareIconClick}
        onDeleteIconClick={onDeleteIconClick}
        onEditIconClick={onEditIconClick}
      />
      <DownloadReportModal
        loading={loading}
        reportName={selectedReport?.name}
        isOpen={showDownloadModal}
        onDownloadButtonClick={onDownloadButtonClick}
        modalCloseClick={onCancelButtonClick}
      />
      <ShareReportModal
        loading={reportSharing}
        reportName={selectedReport?.name}
        email={email}
        isOpen={showShareModal}
        formErrors={errors}
        modalCloseClick={onCancelButtonClick}
        onChangeEmail={onChangeEmail}
        onFormSubmit={onFormSubmit}
      />
      {!hideDeleteButton && (
        <DeleteReportModal
          loading={loading}
          reportName={selectedReport?.name}
          showDeleteModal={showDeleteModal}
          onDeleteButtonClick={onDeleteButtonClick}
          onCancelButtonClick={onCancelButtonClick}
          modalCloseClick={onCancelButtonClick}
        />
      )}
      <GeneralToast id="reports-toast" show={showToast} message={toastMessage} closeToast={onClickToastClose} />
    </>
  );
}

const ReportsTableContainerMemo = memo(ReportsTableContainer, areEqual);

export { ReportsTableContainerMemo as ReportsTableContainer };
